import type { PensionDier } from "../pensionKlantTypes";

/** Wat elke tegel en elk kopje nodig heeft om tekst en datums te maken. */
export type Vertaal = (key: string, options?: Record<string, unknown>) => string;

export interface Context {
  t: Vertaal;
  locale: string;
  /** Vandaag als yyyy-MM-dd; meegegeven zodat tests een vaste dag kunnen kiezen. */
  vandaag: string;
}

export const LEEG = "—";

/** Binnen zoveel dagen heet iets "binnenkort verlopen". */
export const BINNENKORT_DAGEN = 30;

function utc(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`);
}

function geldig(iso: string | null | undefined): iso is string {
  return !!iso && !Number.isNaN(utc(iso).getTime());
}

/** "18 sep" — waar het jaar vanzelf spreekt. */
export function kortDatum(iso: string | null | undefined, locale: string): string {
  if (!geldig(iso)) return LEEG;
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" })
    .format(utc(iso))
    .replace(".", "");
}

/** "08 sep 2026" — zoals in een lijst met gebeurtenissen. */
export function datumMetJaar(iso: string | null | undefined, locale: string): string {
  if (!geldig(iso)) return LEEG;
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })
    .format(utc(iso))
    .replace(".", "");
}

/** "08-09-2014" — een geboortedatum zoals op een formulier. */
export function cijferDatum(iso: string | null | undefined): string {
  if (!geldig(iso)) return LEEG;
  const [j, m, d] = iso.slice(0, 10).split("-");
  return `${d}-${m}-${j}`;
}

/**
 * "18 sep – 27 sep 2026": het jaar één keer, aan het eind. Over de jaargrens heen staat het er twee
 * keer, anders leest "14 mei – 14 mei 2027" als een dag.
 */
export function periode(van: string | null | undefined, tot: string | null | undefined, locale: string): string {
  if (!geldig(van) || !geldig(tot)) return LEEG;
  const begin = van.slice(0, 4) === tot.slice(0, 4) ? kortDatum(van, locale) : datumMetJaar(van, locale).replace(/^0/, "");
  return `${begin} – ${datumMetJaar(tot, locale).replace(/^0/, "")}`;
}

/** Hele dagen van `van` tot `tot`, beide yyyy-MM-dd. */
export function dagenTussen(van: string, tot: string): number {
  return Math.round((utc(tot).getTime() - utc(van).getTime()) / 864e5);
}

/** Hele jaren oud op `vandaag`; `null` zonder (bruikbare) geboortedatum. */
export function leeftijdJaren(geboortedatum: string | null | undefined, vandaag: string): number | null {
  if (!geldig(geboortedatum)) return null;
  const g = utc(geboortedatum);
  const n = utc(vandaag);
  let jaren = n.getUTCFullYear() - g.getUTCFullYear();
  if (n.getUTCMonth() < g.getUTCMonth() || (n.getUTCMonth() === g.getUTCMonth() && n.getUTCDate() < g.getUTCDate())) {
    jaren -= 1;
  }
  return Math.max(0, jaren);
}

/** "12 jaar", of onder het jaar "7 maanden". */
export function leeftijd(geboortedatum: string | null | undefined, { t, vandaag }: Context): string | null {
  const jaren = leeftijdJaren(geboortedatum, vandaag);
  if (jaren == null) return null;
  if (jaren >= 1) return t("pensionKlant.dieren.jaar", { args: [jaren] });
  const maanden = Math.max(0, Math.floor(dagenTussen(geboortedatum!, vandaag) / 30.44));
  return t("pensionKlant.dieren.maanden", { args: [maanden] });
}

export type Staat = "verlopen" | "binnenkort" | "inOrde" | "onbekend";

/** De staat van de entingen: verlopen, binnenkort verlopen, in orde, of niets vastgelegd. */
export function entingStaat(dier: PensionDier, vandaag: string): Staat {
  const s = dier.stand;
  if (!s) return "onbekend";
  if (s.entingenVerlopen > 0) return "verlopen";
  const eerste = s.entingVerlooptOp ?? s.entingGeldigTot;
  if (!eerste) return "onbekend";
  return dagenTussen(vandaag, eerste) <= BINNENKORT_DAGEN ? "binnenkort" : "inOrde";
}

/** Dezelfde vier staten voor een behandeling met een einddatum (ontworming, ontvlooiing). */
export function behandelStaat(behandeld: boolean, geldigTot: string | null | undefined, vandaag: string): Staat {
  if (!behandeld) return "onbekend";
  if (!geldigTot) return "inOrde";
  const over = dagenTussen(vandaag, geldigTot);
  if (over < 0) return "verlopen";
  return over <= BINNENKORT_DAGEN ? "binnenkort" : "inOrde";
}

/** Welke kleur een kenmerk van het dier krijgt. Kleur alleen als het iets betekent. */
export type KenmerkToon = "rood" | "amber" | "groen" | "grijs";

const GEVAAR = /allergi|bijt|agressi|epileps|diabet|medicatie|niet samen|uitbraak|ontsnap|hartpatient|hartpatiënt/i;
const GOED = /^(kan |kan$|goed |sociaal|lief|zindelijk|rustig|mag met|luistert)/i;
const NIETS = /^geen /i;

/**
 * Een allergie of een hond die bijt is rood; "kan met andere honden" is goed nieuws en groen; "geen
 * bijzonderheden" is een mededeling en grijs; de rest vraagt aandacht en is amber. Als alles rood is,
 * valt niets meer op.
 */
export function kenmerkToon(kenmerk: string): KenmerkToon {
  if (GEVAAR.test(kenmerk)) return "rood";
  if (NIETS.test(kenmerk)) return "grijs";
  if (GOED.test(kenmerk)) return "groen";
  return "amber";
}

/** Een lijst uit een antwoord dat er ook anders uit kan zien; anders valt een tegel om op een 404. */
export function lijst<T>(waarde: unknown): T[] {
  return Array.isArray(waarde) ? (waarde as T[]) : [];
}

/** "Mannelijk" of "Vrouwelijk" → ♂ / ♀; anders niets. */
export function geslachtTeken(geslacht: string | null | undefined): string | null {
  const g = geslacht?.toLowerCase() ?? "";
  if (/^(m|reu|kater)/.test(g)) return "♂";
  if (/^(v|teef|poes)/.test(g)) return "♀";
  return null;
}
